import type { MetricItem } from "@/components/key-metrics"
import type { StatusBadgeTone } from "@/components/ui/status-badge"
import {
  evaluationAnalyticsPath,
  evaluationDistributionPath,
  evaluationReviewPath,
  evaluationSchedulePath,
  evaluationTermSurveysPath,
  prismCoursesPath,
} from "@/lib/evaluation-routes"
import { daysUntil } from "@/lib/evaluation-term-utils"
import type { Product } from "@/stores/app-store"
import type { DashboardTermSlice } from "@/lib/evaluation-dashboard-context"
import {
  EVALUATION_REFERENCE_DATE,
  evaluationCoverageCount,
  evaluationCoveragePercent,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import { OPERATIONS_KPI_PRIOR } from "@/lib/mock/evaluation-operations"

export const OPERATIONS_V2_RESPONSE_TARGET = 70
export const OPERATIONS_V2_CLOSING_SOON_DAYS = 7
export const OPERATIONS_V2_QUEUE_VISIBLE_MAX = 6

export type OperationsV2QueueSeverity = "blocked" | "attention" | "time-sensitive" | "ready"

export interface OperationsV2QueueItem {
  id: string
  severity: OperationsV2QueueSeverity
  badgeLabel: string
  tone: StatusBadgeTone
  icon: string
  message: string
  termLabel: string
  ctaLabel: string
  href: string
  secondaryCtaLabel?: string
  secondaryHref?: string
}

export interface OperationsV2OtherTermAction {
  label: string
  href: string
}

export interface OperationsV2OtherTermRow {
  term: EvaluationTerm
  facts: string
  statusNote?: string
  actions: OperationsV2OtherTermAction[]
}

function unpublishedClosedCount(term: EvaluationTerm): number {
  if (term.counts.published > 0) return 0
  return term.counts.closed
}

function closingSoonCount(term: EvaluationTerm, refDate: Date): number {
  if (term.counts.live <= 0 || !term.nextClosingDate) return 0
  const days = daysUntil(term.nextClosingDate, refDate)
  if (days < 0 || days > OPERATIONS_V2_CLOSING_SOON_DAYS) return 0
  return term.counts.live
}

function courseCountLabel(count: number): string {
  return count === 1 ? "1 course" : `${count} courses`
}

function publishHref(
  product: Product,
  currentTerm: EvaluationTerm | undefined,
  lastTerm: EvaluationTerm | undefined,
): string | undefined {
  const lastUnpublished = lastTerm ? unpublishedClosedCount(lastTerm) : 0
  const currentUnpublished = currentTerm ? unpublishedClosedCount(currentTerm) : 0
  if (lastUnpublished > 0 && lastTerm) return evaluationReviewPath(product, lastTerm.id)
  if (currentUnpublished > 0 && currentTerm) {
    return evaluationReviewPath(product, currentTerm.id)
  }
  return undefined
}

export function buildOperationsV2Queue(
  product: Product,
  currentTerm?: EvaluationTerm,
  lastTerm?: EvaluationTerm,
  refDate: Date = EVALUATION_REFERENCE_DATE,
): OperationsV2QueueItem[] {
  const items: OperationsV2QueueItem[] = []

  if (currentTerm && currentTerm.counts.notSetup > 0) {
    const count = currentTerm.counts.notSetup
    items.push({
      id: `${currentTerm.id}-blocked-not-setup`,
      severity: "blocked",
      badgeLabel: "Blocked",
      tone: "danger",
      icon: "fa-triangle-exclamation",
      message:
        count === 1
          ? "1 course has not been scheduled"
          : `${count} courses have not been scheduled`,
      termLabel: currentTerm.termName,
      ctaLabel: count === 1 ? "Schedule 1" : `Schedule ${count}`,
      href: evaluationSchedulePath(product, {
        termId: currentTerm.id,
        stages: "notSetup",
      }),
    })
  }

  if (currentTerm && currentTerm.counts.draft > 0) {
    const count = currentTerm.counts.draft
    items.push({
      id: `${currentTerm.id}-attention-draft`,
      severity: "attention",
      badgeLabel: "Attention",
      tone: "warning",
      icon: "fa-clipboard-list",
      message: count === 1 ? "1 draft needs finishing" : `${count} drafts need finishing`,
      termLabel: currentTerm.termName,
      ctaLabel: count === 1 ? "Finish 1" : `Finish ${count}`,
      href: evaluationSchedulePath(product, {
        termId: currentTerm.id,
        stages: "draft",
      }),
    })
  }

  if (currentTerm && currentTerm.counts.scheduled > 0) {
    const count = currentTerm.counts.scheduled
    const days =
      currentTerm.nextScheduledDate != null
        ? daysUntil(currentTerm.nextScheduledDate, refDate)
        : null
    const timing =
      days == null
        ? ""
        : days === 0
          ? ", next one going out today"
          : days === 1
            ? ", next one going out in 1 day"
            : `, next one going out in ${days} days`
    items.push({
      id: `${currentTerm.id}-scheduled`,
      severity: "attention",
      badgeLabel: "Scheduled",
      tone: "info",
      icon: "fa-calendar-clock",
      message: `Evaluations for ${count} courses are scheduled${timing}`,
      termLabel: currentTerm.termName,
      ctaLabel: "Update",
      href: evaluationDistributionPath(product, {
        termId: currentTerm.id,
        action: "update",
      }),
      secondaryCtaLabel: "Delete",
      secondaryHref: evaluationDistributionPath(product, {
        termId: currentTerm.id,
        action: "delete",
      }),
    })
  }

  if (currentTerm) {
    const closing = closingSoonCount(currentTerm, refDate)
    if (closing > 0) {
      const days = daysUntil(currentTerm.nextClosingDate!, refDate)
      const dayLabel = days === 0 ? "today" : days === 1 ? "in 1 day" : `in ${days} days`
      items.push({
        id: `${currentTerm.id}-closing-soon`,
        severity: "time-sensitive",
        badgeLabel: "Time-sensitive",
        tone: "warning",
        icon: "fa-clock",
        message:
          closing === 1
            ? `1 evaluation closes ${dayLabel}`
            : `${closing} evaluations close ${dayLabel}`,
        termLabel: currentTerm.termName,
        ctaLabel: "Remind or extend",
        href: evaluationDistributionPath(product, {
          termId: currentTerm.id,
          action: "remind",
        }),
      })
    }
  }

  const currentUnpublished = currentTerm ? unpublishedClosedCount(currentTerm) : 0
  const lastUnpublished = lastTerm ? unpublishedClosedCount(lastTerm) : 0
  const readyCount = currentUnpublished + lastUnpublished
  const href = publishHref(product, currentTerm, lastTerm)
  if (readyCount > 0 && href) {
    const termParts = [
      currentUnpublished > 0 && currentTerm ? currentTerm.termName : null,
      lastUnpublished > 0 && lastTerm ? lastTerm.termName : null,
    ].filter((value): value is string => Boolean(value))
    items.push({
      id: "ready-to-publish",
      severity: "ready",
      badgeLabel: "Ready",
      tone: "info",
      icon: "fa-circle-check",
      message:
        readyCount === 1
          ? "1 closed evaluation is ready to publish"
          : `${readyCount} closed evaluations are ready to publish`,
      termLabel: termParts.join(" and "),
      ctaLabel: readyCount === 1 ? "Publish 1" : `Publish ${readyCount}`,
      href,
    })
  }

  return items
}

export function operationsV2QueueCourseCount(
  item: OperationsV2QueueItem,
  currentTerm?: EvaluationTerm,
  lastTerm?: EvaluationTerm,
  refDate: Date = EVALUATION_REFERENCE_DATE,
): number {
  if (item.id.endsWith("-blocked-not-setup")) return currentTerm?.counts.notSetup ?? 0
  if (item.id.endsWith("-attention-draft")) return currentTerm?.counts.draft ?? 0
  if (item.id.endsWith("-scheduled")) return currentTerm?.counts.scheduled ?? 0
  if (item.id.endsWith("-closing-soon") && currentTerm) {
    return closingSoonCount(currentTerm, refDate)
  }
  if (item.id === "ready-to-publish") {
    const currentUnpublished = currentTerm ? unpublishedClosedCount(currentTerm) : 0
    const lastUnpublished = lastTerm ? unpublishedClosedCount(lastTerm) : 0
    return currentUnpublished + lastUnpublished
  }
  return 0
}

export function operationsV2LiveTermMetrics(
  product: Product,
  term: EvaluationTerm,
): MetricItem[] {
  const covered = evaluationCoverageCount(term.counts)
  const coveragePercent = evaluationCoveragePercent(term.counts, term.courseOfferings)
  const setupGap = term.counts.notSetup + term.counts.draft
  const responseRate = term.liveResponseRate
  const closing = closingSoonCount(term, EVALUATION_REFERENCE_DATE)
  const closed = term.counts.closed
  const priorRate = OPERATIONS_KPI_PRIOR.avgResponseRate
  const responseDiff =
    responseRate != null ? responseRate - priorRate : 0
  const belowTarget =
    responseRate != null && responseRate < OPERATIONS_V2_RESPONSE_TARGET

  return [
    {
      id: "coverage",
      label: "Coverage",
      value:
        term.courseOfferings > 0 ? `${covered}/${term.courseOfferings}` : covered,
      delta: "",
      trend: "neutral",
      description:
        setupGap === 0 && term.courseOfferings > 0
          ? "Complete"
          : term.courseOfferings > 0
            ? `${coveragePercent}% covered`
            : undefined,
      href: evaluationSchedulePath(product, {
        termId: term.id,
        stages: "notSetup,draft",
      }),
      progress: covered,
      progressMax: term.courseOfferings > 0 ? term.courseOfferings : undefined,
      progressTone: setupGap > 0 ? "warning" : "success",
      alert: setupGap > 0 ? "warning" : undefined,
    },
    {
      id: "response",
      label: "Response",
      value: responseRate != null ? `${responseRate}%` : "None",
      delta:
        responseRate != null && responseDiff !== 0
          ? responseDiff > 0
            ? `+${responseDiff}`
            : String(responseDiff)
          : "",
      trend:
        responseRate == null || responseDiff === 0
          ? "neutral"
          : responseDiff > 0
            ? "up"
            : "down",
      trendPolarity: "higher_is_better",
      description: `Target ${OPERATIONS_V2_RESPONSE_TARGET}% · vs last week`,
      href: evaluationDistributionPath(product, {
        termId: term.id,
        action: "remind",
      }),
      progress: responseRate,
      progressTone: belowTarget ? "warning" : "success",
      alert: belowTarget ? "warning" : undefined,
    },
    {
      id: "closing-soon",
      label: "Closing soon",
      value: closing,
      delta: "",
      trend: closing > 0 ? "up" : "neutral",
      trendPolarity: "lower_is_better",
      description: `Within ${OPERATIONS_V2_CLOSING_SOON_DAYS} days`,
      href: evaluationDistributionPath(product, {
        termId: term.id,
        action: "remind",
      }),
      alert: closing > 0 ? "warning" : undefined,
    },
    {
      id: "closed",
      label: "Closed",
      value: closed,
      delta: "",
      trend: "neutral",
      description: closed > 0 ? "Ready to review" : "None waiting",
      href: evaluationReviewPath(product, term.id),
    },
  ]
}

function otherTermFacts(term: EvaluationTerm): string {
  if (term.timelinePosition === "upcoming" || term.timelinePosition === "future") {
    const scheduled = evaluationCoverageCount(term.counts)
    return `${scheduled}/${term.courseOfferings} scheduled`
  }
  const parts: string[] = [courseCountLabel(term.courseOfferings)]
  const response =
    term.timelinePosition === "current" && term.liveResponseRate != null
      ? term.liveResponseRate
      : term.closedResponseRate
  if (response != null) parts.push(`${response}% response`)
  if (term.ratings) {
    parts.push(`${term.ratings.programAverage.toFixed(1)} program`)
    parts.push(`${term.ratings.courseAverage.toFixed(1)} course`)
    parts.push(`${term.ratings.facultyAverage.toFixed(1)} faculty`)
  }
  return parts.join(" · ")
}

export function buildOperationsV2OtherTerms(
  product: Product,
  slice: Pick<DashboardTermSlice, "lastTerm" | "upcomingTerms" | "futureTerms">,
): OperationsV2OtherTermRow[] {
  const terms = [
    ...(slice.lastTerm ? [slice.lastTerm] : []),
    ...slice.upcomingTerms,
    ...slice.futureTerms,
  ]

  return terms.map(term => {
    const unpublished = unpublishedClosedCount(term)
    const setupGap = term.counts.notSetup + term.counts.draft
    const actions: OperationsV2OtherTermAction[] = []
    const notes: string[] = []

    if (term.attention) {
      notes.push(
        `${term.attention.coursesBelowThreshold} courses and ${term.attention.facultyRolesBelowThreshold} faculty roles below threshold.`,
      )
    }

    if (term.cardCase === "no-courses" || term.courseOfferings === 0) {
      notes.push("No courses found. Add courses to scheduling evaluations.")
      actions.push({ label: "Add courses", href: prismCoursesPath() })
      return {
        term,
        facts: otherTermFacts(term),
        statusNote: notes.join(" "),
        actions,
      }
    }

    if (term.cardCase === "all-closed" || term.cardCase === "all-published" || term.ratings) {
      actions.push({
        label: "View analytics",
        href: evaluationAnalyticsPath(product, term.id),
      })
      actions.push({
        label: "View surveys",
        href: evaluationTermSurveysPath(product, term.id),
      })
    }

    if (unpublished > 0) {
      notes.push(
        unpublished === 1
          ? "1 result has not been published"
          : `${unpublished} results have not been published`,
      )
      actions.push({
        label:
          unpublished === 1 ? "Publish 1 result" : `Publish ${unpublished} results`,
        href: evaluationReviewPath(product, term.id),
      })
    }

    if (setupGap > 0 && term.cardCase !== "all-closed" && term.cardCase !== "all-published") {
      notes.push("Evaluations have not been scheduled")
      actions.push({
        label: "Schedule evaluations",
        href: evaluationSchedulePath(product, {
          termId: term.id,
          stages: "notSetup,draft",
        }),
      })
    }

    return {
      term,
      facts: otherTermFacts(term),
      statusNote: notes.length > 0 ? notes.join(" ") : undefined,
      actions,
    }
  })
}

export function isLiveResponseBelowTarget(term: EvaluationTerm): boolean {
  return (
    term.liveResponseRate != null &&
    term.liveResponseRate < OPERATIONS_V2_RESPONSE_TARGET
  )
}
